import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy} from '@angular/core';
import {BaseComponent} from '../../../../components/general/base/base.component';
import {_} from '../../../utils';
import {groupBy} from 'lodash-es';
import {takeUntil, filter, scan} from 'rxjs/operators';
import {Subject} from 'rxjs';
import { MapService, MapStyle } from '../../map.service';
import { Layer } from '../../layer/layer.model';
import { LayerGroup } from '../../layer/layer-group.model';


@Component({
  selector: 'qm-map-settings',
  templateUrl: './map-settings.component.html',
  styleUrls: ['./map-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapSettingsComponent extends BaseComponent implements OnInit, OnDestroy {
  /**
   * Current map style
   */
  public currentStyle: MapStyle;

  /**
   * Array of possible map styles
   */
  public styles: MapStyle[] = [];
  /**
   * Visible filters
   */
  public layerGroupMap: Map<string, {group: LayerGroup, expanded: boolean}> = new Map();
  public loadedLayers = new Set();
  public currentZoomLevel: number;
  public notGroupedLayers$ = this.mapService.layersR$.pipe(
    filter(layer =>
      layer.metadata?.group === undefined           // Layer has no group
        && layer.metadata?.userTogglable !== false  // Layer is shown as a button in settings
    ),
    scan((acc: Layer[], layer) => [...acc, layer], [])
  );
  /**
   * Subscription manager
   */
  private ngUnsubscribe = new Subject<void>();

  constructor(private changeDetector: ChangeDetectorRef,
              private mapService: MapService) {
    super();
  }

  ngOnInit() {
    this.styles = this.mapService.getAvailableStyles();
    this.currentStyle = this.mapService.getStyle();

    /**
     * Layers' visible / hidden -status
     */
    this.mapService.layersR$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(layer => {
        layer.isVisible$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(visible => this.changeDetector.markForCheck());
      });

    /**
     * Layers' loading status
     */
    this.mapService.loadedLayers$
      .pipe(
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(layer => {
        this.loadedLayers.add(layer.id);
        this.changeDetector.markForCheck();
      });

      this.mapService.zoom$.pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe(zoomLevel => {
        this.currentZoomLevel = zoomLevel;
        this.changeDetector.markForCheck();
      });

      this.mapService.layerGroups$.pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe(layerGroups => {
        this.layerGroupMap = this.createLayerGroupMap(layerGroups);
        this.changeDetector.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  toggleLayerGroupExpanded(group: any) {
    group.expanded = !group.expanded;
  }

  /**
   * Set map style
   */
  public setStyle(style: MapStyle) {
    this.mapService.setStyle(style);
  }

  public createLayerGroupMap(layerGroups: LayerGroup[]): Map<string, {group: LayerGroup, expanded: boolean}> {
    console.log('get groups');
    const layerGroupMap = new Map();
    const layers = this.mapService.getLayerList().filter((layer: Layer) => {
      return layer.metadata.group;
    });
    const groups = Object.keys(groupBy(layers, 'metadata.group'));
    for (const group of layerGroups) {
      if (group) {
        const temp: Layer[] = [];
        layers.forEach(layer => {
          if (layer.metadata.group && layer.metadata.group === group.name) {
            temp.push(layer);
          }
        });
        layerGroupMap.set(group.name, {group, expanded: false});
      }
    }
    return layerGroupMap;
  }

  toggleGroupVisibility($event: MouseEvent, layerGroup: LayerGroup) {
    layerGroup.setVisibility(($event.target as HTMLInputElement).checked);
  }

  allLayersLoaded(groupName: string): boolean {
    if (this.layerGroupMap.has(groupName)) {
      const layerGroup = this.layerGroupMap.get(groupName);
      const notLoadedLayer = layerGroup.group.layers.find(i => !this.loadedLayers.has(i.id));
      return notLoadedLayer === undefined; // There is no layer that is not loaded
    }
    return false;
  }

  public getVisibleLayerCount(layerGroup: string): string {
    if (this.layerGroupMap.has(layerGroup)) {
      const group = this.layerGroupMap.get(layerGroup);
      return group.group.layers.filter(layer => layer.getIsSelected()).length + '/' + group.group.layers.length;
    }
    return '-';
  }

  zoomTo(event: MouseEvent, layerVisibilityLevel: number) {
    event.stopPropagation();
    this.mapService.zoomTo(layerVisibilityLevel);
  }
  public clearLayerSelections(group: LayerGroup) {
    group.layers.forEach(layer => layer.setIsSelected(false));
  }

  compareFnSelect(s1: {name: string, url: string}, s2: {name: string, url: string}) {
    if (s1 && s2) {
      return s1.url === s2.url;
    }
  }
}
